import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import AlertSignup, { ALERT_HEADLINE, isSubscribed } from "@/components/content/AlertSignup";

const DISMISS_KEY = "pxAlertBarDismissedUntil";
const LEGAL = ["/privacy-policy", "/terms", "/disclaimer", "/disclosures", "/editorial-policy"];

export function openAlertSheet() { window.dispatchEvent(new Event("px-open-alerts")); }

export default function StickyAlertBar() {
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(true);
  const [sheet, setSheet] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = Number(window.localStorage.getItem(DISMISS_KEY) ?? 0) > Date.now(); } catch { /* ignore */ }
    setHidden(dismissed || isSubscribed());
    const sub = () => { setHidden(true); };
    const open = () => { if (window.location.pathname !== "/peptide-horizon") setSheet(true); };
    window.addEventListener("px-subscribed", sub);
    window.addEventListener("px-open-alerts", open);
    return () => { window.removeEventListener("px-subscribed", sub); window.removeEventListener("px-open-alerts", open); };
  }, []);

  const horizon = pathname === "/peptide-horizon";
  const showBar = !hidden && !sheet && !LEGAL.includes(pathname);
  useEffect(() => {
    document.body.classList.toggle("has-alert-bar", showBar);
    return () => document.body.classList.remove("has-alert-bar");
  }, [showBar]);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 864e5)); } catch { /* ignore */ }
    setHidden(true);
  };

  return <>
    {showBar && (
      <div className="alert-bar md:hidden fixed inset-x-0 bottom-0 z-[80] bg-background border-t border-border flex items-center gap-2 px-3">
        <p className="flex-1 text-sm font-medium text-foreground leading-tight">{horizon ? "Get an email when a peptide's status changes." : ALERT_HEADLINE}</p>
        <Button size="sm" className="min-h-10" onClick={() => horizon ? document.getElementById("horizon-signup")?.scrollIntoView({ block: "center" }) : setSheet(true)}>Get email updates</Button>
        <button type="button" onClick={dismiss} aria-label="Dismiss for 7 days" className="min-h-11 min-w-11 flex items-center justify-center text-muted-foreground"><X className="h-4 w-4" /></button>
      </div>
    )}
    {sheet && !horizon && (
      <div className="fixed inset-0 z-[110] flex items-end md:items-center md:justify-center" role="dialog" aria-modal="true" aria-label={ALERT_HEADLINE}>
        <div className="absolute inset-0 bg-foreground/40" onClick={() => setSheet(false)} />
        <div className="relative w-full md:max-w-lg bg-background rounded-t-xl md:rounded-xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
          <button type="button" onClick={() => setSheet(false)} aria-label="Close" className="absolute right-2 top-2 min-h-11 min-w-11 flex items-center justify-center"><X className="h-5 w-5" /></button>
          <AlertSignup variant="sticky" source="peptidex-sticky" />
        </div>
      </div>
    )}
  </>;
}
